import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from "react-chartjs-2";
import shortenLabelName from "./shortenLabelName";
import replaceZentaiToBasename from "./replaceZentaiToBasename";

import "./Band.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const chartPaddingRight = 10;
const options = {
  indexAxis: "y",
  layout: {
    autoPadding: false,
    padding: {
      right: chartPaddingRight,
    },
  },
  elements: {
    bar: {
      borderWidth: 1,
    },
  },
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    yAxis: {
      stacked: true,
      ticks: {
        display: false, // y軸ラベルを表示しない
      },
      grid: {
        drawBorder: false,  // 左端の縦グリッドを表示しない
        tickLength: 0,  //  上余白の削除
        display: false, // 横グリッドを表示しない
      }
    },
    xAxis: {
      stacked: true,
      min: 0,
      max: 100,
      ticks: {
        display: false, // x軸ラベルを表示しない
      },
      grid: {
        drawBorder: false,  // 下端の縦グリッドを表示しない
        tickLength: 0, // 下余白の削除
        display: false, // 縦グリッドを表示しない
      }
    }
  },
  plugins: {
    legend: {
      display: false, // 凡例を表示しない
    },
    // title: {
    //   display: true,
    //   text: '※チャートのデザインは実装途中のものです',
    // },
    tooltip: {
      callbacks: {
        label(ctx) {
          const l = ctx.dataset.label;
          if (ctx.dataIndex === 0) {
            // 凡例のデータは表示しない
            return l;
          } else {
            // 小数第一位まで表示
            const v = ctx.dataset.data[ctx.dataIndex].toFixed(1);
            return `${l}: ${v}`;
          }
        },
      },
    },
    datalabels: {
      font: {
        size: process.env.REACT_APP_IS_EXTERNAL ? 16 : 20,
      },
      padding: 0,

      /* 白抜きの場合 */
      // color: 'white',
      // textStrokeWidth: 1,
      // textStrokeColor: 'black',
      /* 白抜きの場合ここまで */

      /* 白字 or 黒字の場合 */
      color(ctx) {
        if (ctx.datasetIndex === ctx.chart.legend.legendItems.length - 1) {
          return 'black';
        } else {
          return 'white';
        }
      },
      /* 白字 or 黒字の場合ここまで */

      display(ctx) {
        // 凡例のデータラベルは表示しない
        if (ctx.dataIndex === 0) {
          return false;
        }
        // 閾値より小さいデータは表示しない
        const data = ctx.dataset.data[ctx.dataIndex];
        if (data < 1.0) {
          return false;
        }
        // それ以外は自動でオーバーラップを防ぐ設定
        return 'auto';
      },
      formatter(value, ctx) {
        const v = typeof value === "string" ? value : value.toFixed(1);
        if (ctx.dataIndex === 1 && ctx.datasetIndex === 0) {
          return v + '%';
        } else {
          return v;
        }
      },
    },
  },
};

function Band({ json, basename }) {
  const labels = json.labels.map((l) => {
    switch (typeof l) {
      case "string":
        return { "label": l, "n": null };
      case "object":
        return l;
      default:
        console.error("不正なラベル", l);
        return l;
    }
  });
  // 1列目の横幅を決めるために文字数を数える
  const labelMaxLength = labels.reduce((max, cur) => Math.max(max, replaceZentaiToBasename(cur.label, basename).length), 0);

  const backgroundColors = [
    "#2E76B6",
    "#4D8DC5",
    "#77AAD9",
  ];
  const flattenDatasets = json.datasetGroups.map((g) => g.datasets).flat();
  const datasets = flattenDatasets.map((ds, index) => {
    // backgroundColor と borderColor の情報を付与
    let bgcolor;
    if (index === 0) {
      bgcolor = "#08477F";
    } else if (index < flattenDatasets.length - 1) {
      bgcolor = backgroundColors[(index - 1) % backgroundColors.length];
    } else {
      bgcolor = "rgb(255, 255, 255)";
    }
    return {
      ...ds,
      backgroundColor: bgcolor,
      borderColor: "black",
    };
  });

  for (const ds of datasets) {
    // 凡例の作成
    ds.data = [100 / datasets.length, ...ds.data];
  }
  const data = {
    labels: ["凡例", ...labels.map((l) => l.label)],
    datasets: datasets,
  };

  const subtotals = json.subtotals ?? [];

  const preSubtotalCell =
    (subtotals.length > 0) ? <td className="bandchart-pre-subtotal-cell"></td> : null;

  return (
    <div className="bandchart-table-container">
      { subtotals.length > 0 ? <div className="text-right pr-8">(%)</div> : "" }
      <table className="bandchart-chart-table" style={{minWidth: `70rem`}}>
        <tbody>
          <tr>
            <td style={{width: `${0.5+labelMaxLength}em`}}></td>
            <td className="bandchart-second-side-top-cell"></td>
            {json.datasetGroups.map((g, i) => {
              // 表頭のグループの描画
              const classes = ["bandchart-bordered"];
              if (g.label) {
                classes.push("bandchart-full-b");
              } else {
                if (g.datasets.length === 1) {
                  classes.push("bandchart-cap-b");
                } else {
                  classes.push("bandchart-bottom-b")
                }
              }
              return (
                <td key={i} colSpan={g.datasets.length}
                  className={classes.join(" ")}
                > {g.label} </td>
              );
            })}
            {preSubtotalCell}
            {subtotals.map((_, i) =>
              // 小計の行頭の描画
              <td key={i} className="bandchart-subtotal-top-cell"></td>
            )}
          </tr>
          <tr>
            <td></td><td></td>
            {datasets.map((ds, i) =>
              // データセットのラベルの描画
              <td key={i} className="bandchart-bordered bandchart-cup-b">
                {shortenLabelName(ds.label)}
              </td>
            )}
            {preSubtotalCell}
            {subtotals.map((st, i) =>
              // 小計のラベルの描画
              <td key={i}
                  className="bandchart-bordered bandchart-cap-b bandchart-subtotal-label-cell"
              >
                <div className="bandchart-subtotal-label-container">
                  {shortenLabelName(st.label)}
                </div>
              </td>
            )}
          </tr>
          <tr>
            <td className="bandchart-bordered bandchart-bottom-b bandchart-side-cell">
              {/* <div className="bandchart-side-container">{nNode(json.n)}</div> */}
            </td>
            <td className="bandchart-bordered
                  bandchart-bottom-b
                  bandchart-side-cell
                  bandchart-left-align"
            >
              <div className="bandchart-side-container">調査数</div>
            </td>
            <td colSpan={datasets.length}
                rowSpan={labels.length + 1}
                className="bandchart-canvas-cell"
            >
              <div className="bandchart-canvas-container"
                  style={{width: `calc(100% + ${chartPaddingRight}px)`}}
              >
                <Bar
                  key={JSON.stringify(json) /* コンポーネントの引数切替時の強制的に再描画するため key を設定 */}
                  options={options} data={data} plugins={[ChartDataLabels]}
                  style={{position: 'absolute'}}
                  />
              </div>
            </td>
            {preSubtotalCell}
            {subtotals.map((_, i) =>
              // 凡例の帯の右横のセルの描画
              <td key={i} className="bandchart-bordered bandchart-cup-b bandchart-side-cell"></td>
            )}
          </tr>
          {labels.map((l, i) => {
            const baseClasses = ["bandchart-side-cell bandchart-left-align"];
            if (i === labels.length - 1) {
              baseClasses.push("bandchart-bordered bandchart-bottom-b");
            }
            return (
              <tr key={i}>
                <td className={[...baseClasses].join(" ")}>
                  <div className="bandchart-side-container">{replaceZentaiToBasename(shortenLabelName(l.label), basename)}</div>
                </td>
                <td className={[...baseClasses].join(" ")}>
                  <div className="bandchart-side-container bandchart-unbold bandchart-right-align">
                    {(l.n ?? json.n)?.toLocaleString('ja-JP')}
                  </div>
                </td>
                {preSubtotalCell}
                {subtotals.map((st, j) => {
                  const classes = [
                    "bandchart-right-align",
                    "bandchart-bordered",
                    "bandchart-side-cell"
                  ];
                  if (i === labels.length - 1) {
                    classes.push("bandchart-cup-b");
                  } else {
                    classes.push("bandchart-side-b");
                  }
                  return (
                    <td key={j} className={classes.join(" ")}>
                      <div className="bandchart-side-container bandchart-subtotal-score-cell"
                        style={process.env.REACT_APP_IS_EXTERNAL ? { fontSize: "14px" } : { fontSize: "18px" }}>
                        { typeof st.data[i] === "string" ? st.data[i] : st.data[i].toFixed(st.digits ?? 1) }
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default Band;
